import { reportException } from '@/services/sentry.ts';
import { notifications } from '@mantine/notifications';
import { saveAs } from 'file-saver';

const ERROR_BODY_PREVIEW_LENGTH = 300;

export class DatasetFileFetchError extends Error {
  readonly status: number;
  readonly contentType: string | null;
  readonly bodyPreview: string;

  constructor(response: Response, bodyPreview: string) {
    super(`Dataset file fetch failed with status ${response.status}`);
    this.name = 'DatasetFileFetchError';
    this.status = response.status;
    this.contentType = response.headers.get('content-type');
    this.bodyPreview = bodyPreview;
  }
}

async function ensureDatasetFileResponseOk(response: Response) {
  if (response.ok) {
    return;
  }

  let bodyPreview = '';
  try {
    bodyPreview = (await response.text()).slice(0, ERROR_BODY_PREVIEW_LENGTH);
  } catch {
    bodyPreview = '';
  }

  throw new DatasetFileFetchError(response, bodyPreview);
}

export async function fetchDatasetJson(url: string, init?: RequestInit): Promise<unknown> {
  const response = await fetch(url, init);
  await ensureDatasetFileResponseOk(response);
  return response.json();
}

export async function fetchDatasetText(url: string, init?: RequestInit): Promise<string> {
  const response = await fetch(url, init);
  await ensureDatasetFileResponseOk(response);
  return response.text();
}

export async function fetchDatasetBlob(url: string, init?: RequestInit): Promise<Blob> {
  const response = await fetch(url, init);
  await ensureDatasetFileResponseOk(response);
  return response.blob();
}

export async function fetchDatasetArrayBuffer(
  url: string,
  init?: RequestInit,
): Promise<ArrayBuffer> {
  const response = await fetch(url, init);
  await ensureDatasetFileResponseOk(response);
  return response.arrayBuffer();
}

type DownloadDatasetFileOptions = {
  errorTitle?: string;
  errorMessage?: string;
};

function showDatasetDownloadFailure(error: unknown, options: DownloadDatasetFileOptions) {
  reportException(error);
  notifications.show({
    title: options.errorTitle ?? 'Unable to download file',
    message: options.errorMessage ?? 'The download link could not be loaded. Try again.',
    color: 'red',
  });
}

export async function downloadDatasetFile(
  url: string,
  fileName: string,
  options: DownloadDatasetFileOptions = {},
): Promise<boolean> {
  try {
    const blob = await fetchDatasetBlob(url);
    saveAs(blob, fileName);
    return true;
  } catch (error) {
    showDatasetDownloadFailure(error, options);
    return false;
  }
}

type DownloadDatasetTextFileOptions = DownloadDatasetFileOptions & {
  blobType?: string;
  prefix?: string;
};

export async function downloadDatasetTextFile(
  url: string,
  fileName: string,
  options: DownloadDatasetTextFileOptions = {},
): Promise<boolean> {
  try {
    const text = await fetchDatasetText(url);
    const blob = new Blob([options.prefix ?? '', text], {
      type: options.blobType ?? 'text/plain;charset=utf-8;',
    });
    saveAs(blob, fileName);
    return true;
  } catch (error) {
    showDatasetDownloadFailure(error, options);
    return false;
  }
}
